import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { RootDispatch, RootState } from "../../store";
import { getLogsCount } from "../../store/slices/telecallerLeadSlice";
import { fetchDistrictCounts } from "../../api/telecaller.api";
import dayjs from "dayjs";
import TelecallerList from "../../components/Telecaller/TelecallerListForBDM";
import LoadingIndicator from "../../components/AssetsComponent/LoadingSpinner";
import BDMBoardSkeleton from "../../components/Skeleton/BDMBordSkeleton";
import DataEntryPerformance from "../../components/Lead/DataEntryPerformance";
import { CalendarIcon } from "lucide-react";
import { formatDuration } from "../../utils/formatDuration";

type DistrictCount = { district: string; count: number };

interface BDMBoardProps {
  isAdminView?: boolean;
  adminStartDate?: string;
  adminEndDate?: string;
  onDateChange?: (startDate: string, endDate: string) => void;
}

const BDMBoard: React.FC<BDMBoardProps> = ({
  isAdminView = false,
  adminStartDate,
  adminEndDate,
  onDateChange: propOnDateChange,
}) => {
  const dispatch = useDispatch<RootDispatch>();
  const { logsByUser } = useSelector(
    (state: RootState) => state.telecallerLead
  );
  const { dataEntryLeadCount, loading } = useSelector(
    (state: RootState) => state.kanban
  );

  const [localSelectedDate, setLocalSelectedDate] = useState<string>(
    dayjs().format("YYYY-MM-DD")
  );

  const [dataEntryStart, setDataEntryStart] = useState<string>(
    dayjs().format("YYYY-MM-DD")
  );
  const [dataEntryEnd, setDataEntryEnd] = useState<string | undefined>(
    undefined
  );

  const handleDateChange = isAdminView
    ? propOnDateChange
    : (date: string) => setLocalSelectedDate(date);
  const [initialLoading, setInitialLoading] = useState(true);
  const [openUser, setOpenUser] = useState<string | null>(null);
  const [districts, setDistricts] = useState<DistrictCount[]>([]);
  const [loadingDistrictsUser, setLoadingDistrictsUser] = useState<
    string | null
  >(null);
  const [startDate, setStartDate] = useState<string>(
    dayjs().format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState<string | undefined>(undefined);

  useEffect(() => {
    setInitialLoading(true);
    if (!isAdminView) {
      dispatch(getLogsCount({ startDate, endDate }))
        .unwrap()
        .finally(() => {
          setInitialLoading(false);
        });
    } else {
      setInitialLoading(false);
    }
  }, [dispatch, startDate, endDate, isAdminView]);

  // 2) Full-page skeleton
  if (initialLoading) {
    return <BDMBoardSkeleton />;
  }

  const toggleDistricts = async (userId: string) => {
    if (openUser === userId) {
      setOpenUser(null);
      return;
    }
    setLoadingDistrictsUser(userId);
    try {
      if (isAdminView) {
        const data = await fetchDistrictCounts(
          userId,
          adminStartDate,
          adminEndDate
        );
        setDistricts(data);
      } else {
        const data = await fetchDistrictCounts(userId, startDate, endDate);
        setDistricts(data);
      }
      setOpenUser(userId);
    } finally {
      setLoadingDistrictsUser(null);
    }
  };

  function getCountBadgeClass(count: number): string {
    if (count <= 50) return "bg-orange-100 text-gray-800";
    if (count >= 75) return "bg-green-100 text-gray-800";
    return "bg-gray-100 text-gray-800";
  }

  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="bg-gray-50 p-6">
      <div className="mx-auto">
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-1">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-xl shadow p-6">
                <h3 className="text-gray-500 text-sm font-medium">
                  Total Telecallers
                </h3>
                <p className="text-xl font-bold text-gray-800 mt-2">
                  {logsByUser.length}
                </p>
              </div>
              <div className="bg-white rounded-xl shadow p-6">
                <h3 className="text-gray-500 text-sm font-medium">
                  Total Calls Today
                </h3>
                <p className="text-xl font-bold text-gray-800 mt-2">
                  {logsByUser.reduce((sum, u) => sum + u.count, 0)}
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow overflow-hidden mb-8">
              <div className="p-6 border-b flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-800">
                  Telecaller Performance
                </h2>
                {!isAdminView ? (
                  <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4 bg-white rounded-2xl ">
                    <div className="flex flex-col">
                      <label
                        htmlFor="startDate"
                        className="text-xs font-medium text-gray-700 mb-1"
                      >
                        Start Date
                      </label>
                      <div className="relative">
                        <input
                          id="startDate"
                          type="date"
                          value={startDate}
                          max={today}
                          onChange={(e) => setStartDate(e.target.value)}
                          className="px-4  py-1.5 w-40 text-gray-800 text-xs bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                        />
                      </div>
                    </div>
                    <div className="flex flex-col">
                      <label
                        htmlFor="endDate"
                        className="text-xs font-medium text-gray-700 mb-1"
                      >
                        End Date
                      </label>
                      <div className="relative">
                        <input
                          id="endDate"
                          type="date"
                          value={endDate}
                          max={today}
                          onChange={(e) => setEndDate(e.target.value)}
                          placeholder="(optional)"
                          className="px-4  py-1.5 w-40 text-gray-800 text-xs bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4 bg-white  rounded-2xl s">
                    <div className="flex flex-col">
                      <label
                        htmlFor="adminStartDate"
                        className="text-xs font-medium text-gray-700 mb-1"
                      >
                        Start Date
                      </label>
                      <div className="relative">
                        <input
                          id="adminStartDate"
                          type="date"
                          value={adminStartDate}
                          max={today}
                          onChange={(e) =>
                            propOnDateChange?.(
                              e.target.value,
                              adminEndDate || ""
                            )
                          }
                          className="px-4  py-1.5 w-40 text-gray-800 text-xs bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                        />
                      </div>
                    </div>
                    <div className="flex flex-col">
                      <label
                        htmlFor="adminEndDate"
                        className="text-xs font-medium text-gray-700 mb-1"
                      >
                        End Date
                      </label>
                      <div className="relative">
                        <input
                          id="adminEndDate"
                          type="date"
                          value={adminEndDate}
                          max={today}
                          onChange={(e) =>
                            propOnDateChange?.(
                              adminStartDate || "",
                              e.target.value
                            )
                          }
                          placeholder="(optional)"
                          className=" px-4 py-1.5 w-40 text-gray-800 text-xs bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {[
                        "Telecaller",
                        "Call Count",
                        "Not Picked Up",
                        "Duration",
                        "Actions",
                      ].map((col) => (
                        <th
                          key={col}
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {logsByUser.map((u) => (
                      <React.Fragment key={u.userId}>
                        <tr className="hover:bg-gray-50 transition-colors">
                          {/* Name */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-medium">
                                {u.username.charAt(0).toUpperCase()}
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {u.username.capitalizeWords()}
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* Call Count */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getCountBadgeClass(
                                u.count
                              )}`}
                            >
                              {u.count} Calls
                            </span>
                          </td>

                          {/* Not picked */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getCountBadgeClass(
                                u.unpickedCount
                              )}`}
                            >
                              {u.unpickedCount} Missed
                            </span>
                          </td>

                          {/* Duration */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                              {u.totalDuration
                                ? formatDuration(u.totalDuration)
                                : "0:00"}
                            </span>
                          </td>

                          {/* Actions */}
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => toggleDistricts(u.userId)}
                              disabled={loadingDistrictsUser === u.userId}
                              className="flex items-center text-blue-600 hover:text-blue-900 disabled:opacity-50"
                            >
                              {loadingDistrictsUser === u.userId ? (
                                <LoadingIndicator text="Loading..." />
                              ) : openUser === u.userId ? (
                                "Hide details"
                              ) : (
                                "View details"
                              )}
                            </button>
                          </td>
                        </tr>

                        {/* Expanded row */}
                        {openUser === u.userId && (
                          <>
                            <td colSpan={4} className="p-4 ">
                              <div className="bg-white flex items-center gap-2 rounded-xl  w-full">
                                <h3 className="text-gray-500 text-xs font-medium uppercase">
                                  Active Districts:
                                </h3>
                                <p className="text-xs font-bold text-gray-800">
                                  {
                                    new Set(districts.map((d) => d.district))
                                      .size
                                  }
                                </p>
                              </div>
                            </td>

                            <tr>
                              <td colSpan={4} className="px-6 py-4 bg-gray-50">
                                {loadingDistrictsUser === u.userId ? (
                                  <div className="flex justify-center py-4">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
                                  </div>
                                ) : districts.length ? (
                                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {districts.map((d) => (
                                      <div
                                        key={d.district}
                                        className="bg-white p-4 rounded-lg shadow border"
                                      >
                                        <h5 className="text-xs font-medium text-gray-700">
                                          {d.district.capitalizeWords()}
                                        </h5>
                                        <p className="text-xs font-bold text-blue-600 mt-1">
                                          {d.count} Calls
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-gray-500 text-sm py-2">
                                    No calls recorded in any district for this
                                    date.
                                  </p>
                                )}
                              </td>
                            </tr>
                          </>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Right column */}
          {!isAdminView && (
            <div className="w-full lg:w-[45%] xl:w-[45%]">
              <div className="bg-white rounded-xl shadow overflow-hidden sticky top-6 mb-4">
                <div className="p-6 border-b">
                  <h2 className="text-xl font-semibold text-gray-800">
                    Your Telecallers
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    View and manage telecallers under your supervision
                  </p>
                </div>
                <div className="p-6">
                  <TelecallerList />
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <DataEntryPerformance
                  startDate={dataEntryStart}
                  endDate={dataEntryEnd}
                  onDateChange={(s, e) => {
                    setDataEntryStart(s);
                    setDataEntryEnd(e);
                  }}
                  data={dataEntryLeadCount}
                  loading={loading}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BDMBoard;
